import { useEffect, useRef, useState } from 'react';
import Ballot, { BallotCategory, DEFAULT_SELECT_VALUE } from './Ballot';
import { fetchApi } from './utils';

type MultiSelectDropdownProps = {
  id: string;
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
}

function MultiSelectDropdown({ id, options, selected, onChange }: MultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggle = (option: string) => {
    const next = selected.includes(option)
      ? selected.filter(s => s !== option)
      : [...selected, option];
    onChange(next);
  };

  const label = selected.length === 0 ? DEFAULT_SELECT_VALUE
    : selected.length === 1 ? selected[0]
    : `(${selected.length} selected)`;

  return (
    <div className="multiselect-dropdown" ref={ref}>
      <button type="button" id={id} className="multiselect-trigger"
        onClick={() => setIsOpen(o => !o)}>
        {label}
      </button>
      {isOpen && (
        <div className="multiselect-options">
          {options.map(opt => (
            <label key={opt} className="multiselect-option">
              <input
                type="checkbox"
                checked={selected.includes(opt)}
                onChange={() => toggle(opt)}
              />
              {opt}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// MasterBallot: a Ballot variant with no username input, multi-select per
// category (to support ties), and a different submit endpoint.
class MasterBallot extends Ballot {
  getUsername = () => 'MASTER';
  getTopLevelClass = () => "page master";
  getSubmitEndpoint = () => 'submit/master';

  componentDidMount() {
    document.title = 'Awards Site : MASTER';
    super.componentDidMount();
  }

  getNameField() {
    return <></>;
  }

  // Never disable selects in MasterBallot
  shouldDisableCategory(_category: BallotCategory) {
    return false;
  }

  // Seed the category map from the current winners[] rather than current_value,
  // since after migration MASTER has no entries row.
  buildCategoryMap(categories: BallotCategory[]): Map<string, string[]> {
    const categoryMap = new Map<string, string[]>();
    for (const category of categories) {
      categoryMap.set(
        category.category,
        category.winner && category.winner.length > 0 ? [...category.winner] : [],
      );
    }
    return categoryMap;
  }

  handleMultiChange = (category: string, values: string[]) => {
    this.setState({
      ...this.state,
      categories: this.state.categories.set(category, values),
      hasChanges: true,
    });
  };

  // Submit using { category, choices: string[] } instead of { category, choice }.
  handleSubmit(event: any) {
    event.preventDefault();
    const ballotBody = { choices: new Array<{ category: string, choices: string[] }>() };
    this.state.categories.forEach((val, cat) => {
      // Always include every category, even if empty, so the backend can clear
      // previously-set winners when the admin deselects everything.
      ballotBody.choices.push({ category: cat, choices: val });
    });
    const options = {
      method: 'PUT',
      body: JSON.stringify(ballotBody),
      headers: { 'Content-Type': 'application/json' },
    };
    fetchApi(this.getSubmitEndpoint(), this.state.show, options)
      .then(() => {
        this.setState({ hasChanges: false });
      })
      .catch((error) => {
        console.error(error);
      });
  }

  renderCategory = (category: BallotCategory) => {
    const selected = this.state.categories.get(category.category) || [];
    return (
      <>
        <label className="awardname" htmlFor={category.category}>
          {category.category_name}
          <span className="pointvalue">
            ({category.points}&nbsp;pt{category.points > 1 ? 's' : ''})
          </span>
        </label>
        <div className="selection">
          <MultiSelectDropdown
            id={category.category}
            options={category.nominees}
            selected={selected}
            onChange={(values) => this.handleMultiChange(category.category, values)}
          />
        </div>
      </>
    );
  }
}

export default MasterBallot;
